import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PriceTag } from "@/components/PriceTag";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";
import { usePersona } from "@/lib/persona";

/**
 * /app/wallet — "My tickets". Real data from GET /api/v1/me/pass.
 *
 * Replaces the M1 stub. Shows each ticket with its QR (hosted image for now;
 * the ECDSA-signed offline credential lands later — the QR payload TOT:<id>
 * is already the stable scan token, so gate-side validation can start with a
 * simple lookup and upgrade to signature checks without re-issuing passes).
 *
 * Attendee-only route. Other personas get a silent redirect to /app.
 */

export const Route = createFileRoute("/app/wallet")({
  component: WalletPage,
});

interface Pass {
  ticket_id: string;
  status: string;
  product: { id: string; name: string; access_level_code: string };
  purchase: { id: string; total_cents: number; currency: string; purchased_at: string };
  entitlement: {
    resource_type: string; valid_from: string | null; valid_to: string | null;
    activation_status: "waiting" | "ready" | "active" | "expired";
    activation_available_at: string; festival_closes_at: string;
  } | null;
  qr: { token: string; image_url: string };
}

interface PassResp {
  passes: Pass[];
  has_active_pass: boolean;
}

function WalletPage() {
  const persona = usePersona();
  const [data, setData] = useState<PassResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [activating, setActivating] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    await api<PassResp>("/api/v1/me/pass")
      .then(setData)
      .catch((e) => {
        setError(e instanceof ApiError ? (e.problem.detail ?? e.message) : String(e));
      });
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function activate(ticketId: string) {
    setActivating(ticketId); setError(null);
    try {
      await api("/api/v1/me/pass/activate", { method: "POST", json: { ticket_id: ticketId } });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? (e.problem.detail ?? e.message) : String(e));
    } finally { setActivating(null); }
  }

  if (persona.role !== "attendee") return <Navigate to="/app" replace />;

  return (
    <div className="container max-w-2xl py-6">
      <h1 className="mb-6 font-serif text-3xl text-palm">My tickets</h1>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!data && !error && <p className="text-muted-foreground">Loading…</p>}

      {data && data.passes.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>No tickets yet</CardTitle>
            <CardDescription>
              Grab a pass and it will appear here with its QR code, ready to scan at any
              partner venue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to={"/passes" as never}>See passes</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        {data?.passes.map((p) => (
          <Card key={p.ticket_id}>
            <CardHeader>
              <CardTitle className="font-serif text-2xl text-palm">{p.product.name}</CardTitle>
              <CardDescription>
                {p.entitlement?.activation_status === "active" ? "Active" : "Purchased"} ·{" "}
                <PriceTag cents={p.purchase.total_cents} currency={p.purchase.currency} /> · purchased{" "}
                {new Date(p.purchase.purchased_at).toLocaleDateString("en-US", {
                  month: "long", day: "numeric", year: "numeric",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-3">
              {/* QR only renders while the entitlement is ACTIVE. An expired
                  or missing entitlement shows a notice instead — no scannable
                  code for a pass that won't open doors (per Manuel). */}
              {p.entitlement?.activation_status === "active" ? (
                <>
                  <img
                    src={p.qr.image_url}
                    alt={`Pass QR ${p.qr.token}`}
                    width={220}
                    height={220}
                    className="rounded-lg border bg-white p-3"
                    loading="lazy"
                  />
                  <p className="text-center text-xs text-muted-foreground">
                    Show at any partner venue. Screenshot it so you don't need signal on-site.
                  </p>
                  <p className="text-center text-xs font-medium text-palm">
                    Active until {new Date(p.entitlement.valid_to!).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })}
                  </p>
                </>
              ) : p.entitlement?.activation_status === "ready" ? (
                <div className="w-full space-y-4 rounded-lg border border-palm/30 bg-sand/40 p-4">
                  <div>
                    <p className="font-medium text-palm">Ready to activate</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {p.product.name === "Day Pass" && "Your pass will be active for today, until midnight in Tulum."}
                      {p.product.name === "Weekly Pass" && "Your pass will be active for seven days from the moment you activate it."}
                      {p.product.name === "Full Festival Pass" && "Your pass will be active for one month from activation, limited to the festival closing time."}
                    </p>
                  </div>
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1" checked={!!confirmed[p.ticket_id]}
                      onChange={(e) => setConfirmed((v) => ({ ...v, [p.ticket_id]: e.target.checked }))} />
                    <span>I understand that activation starts now and cannot be restarted.</span>
                  </label>
                  <Button className="w-full" disabled={!confirmed[p.ticket_id] || activating !== null}
                    onClick={() => void activate(p.ticket_id)}>
                    {activating === p.ticket_id ? "Activating…" : "Activate my pass"}
                  </Button>
                </div>
              ) : p.entitlement?.activation_status === "waiting" ? (
                <div className="w-full rounded-lg border border-dashed bg-muted/40 px-4 py-6 text-center">
                  <p className="font-medium text-palm">Purchased and ready for the festival</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Activation opens {new Date(p.entitlement.activation_available_at).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })}.
                    Your activation time will not start before the festival.
                  </p>
                </div>
              ) : (
                <div className="w-full rounded-lg border border-dashed bg-muted/40 px-4 py-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    This pass is no longer active. The festival window it covered has ended.
                  </p>
                </div>
              )}
              <p className="font-mono text-[11px] text-muted-foreground">{p.ticket_id}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
